import React, { useState } from 'react';
import {
  Autocomplete,
  TextField,
  FormControl,
  FormHelperText,
  Box,
} from '@mui/material';

export default function AppSearchSelect({
  field,
  data,
  key,
  t,
  errorMessage,
  disabled = false,
  required = false,
  label,
  placeholder = '',
  acceptedValues = [],
  value,
  onChange,
  onBlur,
  name,
  id
}) {
  const [inputValue, setInputValue] = useState('');

  // Transform acceptedValues to work with Autocomplete
  const options = acceptedValues.map((value) => {
    if (typeof value === 'string') {
      return { label: value, value: value };
    }
    return {
      label: value?.key || value?.label || value,
      value: value?.value || value
    };
  });

  // Find the current option based on the field value
  const currentOption = options.find(option => option.value === field.value) || null;

  const handleChange = (event, newValue) => {
    const selectedValue = newValue?.value || '';
    // Call field.onChange with the proper event structure for Formik
    field.onChange({
      target: {
        name: field.name,
        value: selectedValue
      }
    });
  };

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  // Check if field is required and empty, or has an error message
  const isEmpty = !field.value || field.value === '';
  const showError = (required && isEmpty) || (errorMessage && errorMessage.trim() !== '');

  return (
    <Box>  
      <FormControl
        fullWidth
        variant="outlined"
        required={required}
        disabled={disabled}
        error={showError}
      >
        <Autocomplete
          id={id}
          name={field.name}
          value={currentOption}
          onChange={handleChange}
          onInputChange={handleInputChange}
          onBlur={field.onBlur}
          inputValue={inputValue}
          options={options}
          getOptionLabel={(option) => option?.label || option || ''}
          isOptionEqualToValue={(option, value) => option?.value === value?.value}
          disabled={disabled}
          freeSolo={false}
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              autoComplete="off"
              error={showError}
              required={required}
              InputProps={{
                ...params.InputProps,
                style: {
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#F8F8F8'
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: showError ? '#FF9494' : 'rgba(0, 136, 245, 0.7)',
                  },
                  '&:hover fieldset': {
                    borderColor: showError ? '#FF9494' : 'rgba(0, 136, 245, 0.7)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: showError ? '#FF9494' : 'rgba(0, 136, 245, 0.7)',
                  },
                },
                '& .MuiInputBase-input': {
                  color: '#F8F8F8',
                }
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box
              {...props}
              component="li"
              sx={{
                color: '#F8FAFF',
                backgroundColor: 'transparent',
                maxWidth: 420,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                padding: '8px 12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: '#0088F5',
                  color: '#F8F8F8',
                }
              }}
            >
              {option.label}
            </Box>
          )}
          ListboxProps={{
            style: {
              maxHeight: 150,
              overflowY: 'auto',
              padding: 0
            }
          }}
          PaperComponent={({ children, ...other }) => (
            <div 
              {...other} 
              style={{ 
                backgroundColor: 'rgba(4, 25, 57)',
                borderRadius: '4px',
                boxShadow: '0px 0px 8px rgba(87, 87, 245, 0.14)',
                overflow: 'hidden'
              }}
            >
              {children}
            </div>
          )}
        />
      </FormControl>
      
      <FormHelperText
        sx={{ color: "#FF9494", fontSize: 12 }}
      >
        {errorMessage ? errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1) : ""}
      </FormHelperText>
    </Box>
  );
}
